<template>
  <v-card flat>
    <v-overlay
      class="align-center justify-center"
      :model-value="loading"
      :opacity=".7"
      contained
      scrim="surface"
    >
      <div class="text-center">
        <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
        />
      </div>
    </v-overlay>
    <OpenBalancesTable
      v-model="tableOptions"
      :items="items"
      :filter-loading="filterLoading && !loading"
      :is-export-all-loading="isExportAllOpenInvoicesLoading"
      :export-loading-key="exportLoadingKey"
      @click:export-all="exportAllOpenInvoices(false)"
      @click:export-all-detailed="exportAllOpenInvoices(true)"
      @click:export-single="exportSingleOpenBalance"
    />
  </v-card>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';

import { ITableOptions } from '@/bundles/BaseTable/interfaces';
import { ExportRecordType, ExportType } from '@/bundles/Common/types';
import { ServiceFactory } from '@/services/ServiceFactory';
import { Nullable } from '@/utils/types';
import { useNotifications } from '@/bundles/App/composables/useNotifications';

import OpenBalancesTable from '@/components/reports/OpenBalancesTable.vue';

const SavedSetService = ServiceFactory.get('savedSets');

interface IData {
  isExportAllOpenInvoicesLoading: boolean;
  exportLoadingKey: Nullable<string>;
}

export default defineComponent({
  name: 'OpenBalancesTableCard',

  components: { OpenBalancesTable },

  props: {
    loading: Boolean,
    filterLoading: Boolean,
    modelValue: {
      type: Object as PropType<ITableOptions>,
      required: true
    },
    items: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    fetchParams: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({})
    }
  },

  emits: ['update:modelValue'],

  setup () {
    const { addNotification, addSuccessNotification } = useNotifications();

    return { addNotification, addSuccessNotification };
  },

  data: (): IData => ({
    isExportAllOpenInvoicesLoading: false,
    exportLoadingKey: null,
  }),

  computed: {
    tableOptions: {
      get (): ITableOptions {
        return this.modelValue;
      },
      set (val: ITableOptions) {
        this.$emit('update:modelValue', val);
      }
    }
  },

  methods: {
    async exportAllOpenInvoices (isDetailed: boolean) {
      try {
        this.isExportAllOpenInvoicesLoading = true;

        const { dateFrom, dateTo, ...rest } = this.fetchParams;

        const payload = {
          record_type: ExportRecordType.open_balances,
          type: ExportType.list_export,
          show_financials: isDetailed,
          records: [],
          date_from: dateFrom,
          date_to: dateTo,
          ...rest,
        };

        await SavedSetService.exportToExcelNew(payload);
        this.addSuccessNotification('Your export <b>is being generated</b>. When <b>complete</b>, a notification <b>will appear</b> at the <b>top right</b> of the screen. ' +
            'Some exports take a few minutes to generate.');
      } catch (error) {
        this.addNotification({ ...error, status: 0 });
      } finally {
        this.isExportAllOpenInvoicesLoading = false;
      }
    },
    async exportSingleOpenBalance (recordId: string) {
      try {
        this.exportLoadingKey = recordId;
        const payload = {
          record_type: ExportRecordType.open_balances,
          type: ExportType.list_export,
          show_financials: true,
          records: [recordId],
        };

        await SavedSetService.exportToExcelNew(payload);
        this.addSuccessNotification('Your export <b>is being generated</b>. When <b>complete</b>, a notification <b>will appear</b> at the <b>top right</b> of the screen. ' +
          'Some exports take a few minutes to generate.');
      } catch (error) {
        this.addNotification({ ...error, status: 0 });
      } finally {
        this.exportLoadingKey = null;
      }
    },
  }
})
</script>
